import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';

export default function PricingPage() {
  const { isLightweightAccount, isSubscriber } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubscribe() {
    if (isLightweightAccount) {
      navigate('/signup?upgrade=true');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/stripe/create-checkout', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not start checkout');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  return (
    <DashboardLayout>
      <div style={styles.header}>
        <h1 style={styles.heading}>Subscribe to Classroom.Bingo</h1>
        <p style={styles.sub}>Full library access for one flat annual fee.</p>
      </div>

      {isSubscriber && (
        <div style={styles.activeBanner}>
          You already have an active subscription. Thanks for supporting Classroom.Bingo!
        </div>
      )}

      <div style={styles.card}>
        <div style={styles.priceRow}>
          <span style={styles.price}>$19.99</span>
          <span style={styles.period}>&nbsp;/ year</span>
        </div>
        <p style={styles.renewal}>Billed annually. Cancel any time from your account settings.</p>

        <ul style={styles.featureList}>
          <li style={styles.feature}>
            <span style={styles.check}>✓</span>
            Full novel library — all titles, including new additions
          </li>
          <li style={styles.feature}>
            <span style={styles.check}>✓</span>
            Custom vocabulary uploads (CSV import)
          </li>
          <li style={styles.feature}>
            <span style={styles.check}>✓</span>
            Saved custom word lists — access from any device
          </li>
          <li style={styles.feature}>
            <span style={styles.check}>✓</span>
            School name on Stripe receipts for reimbursement
          </li>
          <li style={styles.feature}>
            <span style={styles.check}>✓</span>
            TpT-unlocked novels remain free regardless of subscription
          </li>
        </ul>

        {error && <p style={styles.error}>{error}</p>}

        <button
          onClick={handleSubscribe}
          disabled={loading || isSubscriber}
          style={{
            ...styles.button,
            ...(loading || isSubscriber ? styles.buttonDisabled : {}),
          }}
        >
          {loading
            ? 'Redirecting to checkout…'
            : isLightweightAccount
            ? 'Create a full account first →'
            : isSubscriber
            ? 'Already subscribed'
            : 'Subscribe for $19.99/yr →'}
        </button>

        <p style={styles.secureNote}>
          Payments processed securely by Stripe. No card data touches our server.
        </p>
      </div>

      <div style={styles.faqSection}>
        <h2 style={styles.faqHeading}>Questions</h2>
        <div style={styles.faq}>
          <p style={styles.faqQ}>What happens if I cancel?</p>
          <p style={styles.faqA}>
            You keep full access until the end of your current billing year. After that, your account
            reverts to the TpT-only tier — any novels you unlocked via TpT remain free forever.
            Your custom word lists are preserved for up to two years in case you re-subscribe.
          </p>
        </div>
        <div style={styles.faq}>
          <p style={styles.faqQ}>Can I get a receipt for school reimbursement?</p>
          <p style={styles.faqA}>
            Yes. During checkout you can enter your school name in the billing address field.
            Stripe sends a receipt to your email automatically after payment.
          </p>
        </div>
        <div style={styles.faq}>
          <p style={styles.faqQ}>Does it auto-renew?</p>
          <p style={styles.faqA}>
            Yes — Stripe bills your card annually. You can cancel any time from Account Settings
            and your access continues through the paid period.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}

const styles = {
  header: { marginBottom: '32px' },
  heading: { fontSize: '28px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 4px' },
  sub: { fontSize: '15px', color: '#6b6b7a', margin: 0 },
  activeBanner: {
    backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: '10px',
    padding: '14px 20px', marginBottom: '24px', fontSize: '14px',
    color: '#15803d', fontWeight: '500',
  },
  card: {
    backgroundColor: '#fff', border: '2px solid #1a1a2e', borderRadius: '14px',
    padding: '36px', maxWidth: '520px', marginBottom: '48px',
  },
  priceRow: { display: 'flex', alignItems: 'baseline', marginBottom: '4px' },
  price: { fontSize: '48px', fontWeight: '800', color: '#1a1a2e', lineHeight: 1 },
  period: { fontSize: '20px', color: '#6b6b7a', fontWeight: '500' },
  renewal: { fontSize: '13px', color: '#9ca3af', margin: '0 0 28px' },
  featureList: { listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: '12px' },
  feature: { display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', color: '#374151', lineHeight: '1.5' },
  check: { color: '#16a34a', fontWeight: '700', flexShrink: 0, marginTop: '1px' },
  error: { fontSize: '13px', color: '#dc2626', margin: '0 0 16px' },
  button: {
    display: 'block', width: '100%', padding: '14px 24px',
    backgroundColor: '#1a1a2e', color: '#fff', border: 'none',
    borderRadius: '10px', fontWeight: '700', fontSize: '16px',
    cursor: 'pointer', textAlign: 'center', transition: 'background 0.15s',
  },
  buttonDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  secureNote: { fontSize: '12px', color: '#9ca3af', margin: '12px 0 0', textAlign: 'center' },
  faqSection: { maxWidth: '520px' },
  faqHeading: { fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 20px' },
  faq: { marginBottom: '20px' },
  faqQ: { fontSize: '14px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px' },
  faqA: { fontSize: '14px', color: '#6b6b7a', margin: 0, lineHeight: '1.6' },
};
